/**
 * Lignes Excel export Trip Check Claims — GET /api/trips/check-claims
 * En-têtes = noms de champs API (hors montants de claims).
 */

const pickFirstDefined = (...values) => {
    for (const value of values) {
        if (value !== null && value !== undefined && value !== '') {
            return value;
        }
    }
    return '';
};

/** Accepte 0 / false comme valeurs valides. */
const pickIncludingZero = (...values) => {
    for (const value of values) {
        if (value === 0 || value === '0' || value === false || value === true) return value;
        if (value !== null && value !== undefined && value !== '') {
            return value;
        }
    }
    return '';
};

const formatWeatherParameter = (value) => {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

const collectWeatherParameters = (sub) => {
    if (Array.isArray(sub?.weather_parameter) && sub.weather_parameter.length > 0) {
        return sub.weather_parameter;
    }
    if (sub?.weather_parameter != null && sub.weather_parameter !== '') {
        return [sub.weather_parameter];
    }
    return [];
};

const hasWeatherParam = (params, needle) =>
    params.some((p) => String(p).toLowerCase().includes(String(needle).toLowerCase()));

const stringifyJson = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const buildActivityCombinedAddress = (ai = {}) => {
    const parts = [
        ai.activity_address,
        ai.activity_zip_code,
        ai.activity_city,
        ai.activity_country,
    ]
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter((v) => v !== '');
    return parts.join(', ');
};

const getFirstSubcoverage = (item) => {
    const ri = item?.reference_info || {};
    if (Array.isArray(ri.subcoverage_trips) && ri.subcoverage_trips.length > 0) {
        return ri.subcoverage_trips[0];
    }
    if (Array.isArray(ri.SubCoverage) && ri.SubCoverage.length > 0) {
        return ri.SubCoverage[0];
    }
    return item?.subcoverage_trip || {};
};

/**
 * @param {object} item — élément de check_claims
 * @returns {object} ligne Excel (clés = noms API)
 */
export const buildTripsCheckClaimExcelRow = (item) => {
    const ai = item?.activity_info || {};
    const ci = item?.client_info || {};
    const pr = item?.partner_info || {};
    const ri = item?.reference_info || {};
    const pi = item?.policy_info || {};
    const fi = item?.forecast_info || {};
    const checkClaims = item?.CheckClaims || {};
    const sub = getFirstSubcoverage(item);

    const weatherParams = collectWeatherParameters(sub);
    const hasPrecip = weatherParams.length === 0 || hasWeatherParam(weatherParams, 'precip');
    const hasTemp = weatherParams.length === 0 || hasWeatherParam(weatherParams, 'temp');
    const precipOnly = hasPrecip && !hasWeatherParam(weatherParams, 'temp') && weatherParams.length > 0;
    const tempOnly = hasTemp && !hasWeatherParam(weatherParams, 'precip') && weatherParams.length > 0;

    // Intensité par heure : prcp_trigger_hour_inf (pas trigger_low, souvent null sur Flower)
    const triggerHourInf = pickFirstDefined(
        item?.prcp_trigger_hour_inf,
        checkClaims.prcp_trigger_hour_inf,
        sub.prcp_trigger_hour_inf
    );
    const triggerLow = pickFirstDefined(
        item?.trigger_low,
        checkClaims.trigger_low,
        sub.trigger_low
    );

    let temperature_is_triggered_forecast = '';
    let temperature_count_hours_triggered = '';
    if (!precipOnly) {
        temperature_is_triggered_forecast = pickIncludingZero(
            item?.temperature_is_triggered_forecast,
            checkClaims.temperature_is_triggered_forecast
        );
        temperature_count_hours_triggered = pickIncludingZero(
            item?.temperature_count_hours_triggered,
            checkClaims.temperature_count_hours_triggered
        );
    }

    let precipitation_is_triggered_forecast = '';
    let precipitation_count_hours_triggered = '';
    if (!tempOnly) {
        precipitation_is_triggered_forecast = pickIncludingZero(
            item?.precipitation_is_triggered_forecast,
            checkClaims.precipitation_is_triggered_forecast
        );
        // Si seul precip et flag null → repli is_triggered_forecast
        if (
            precipOnly
            && (precipitation_is_triggered_forecast === '' || precipitation_is_triggered_forecast == null)
        ) {
            precipitation_is_triggered_forecast = pickIncludingZero(
                item?.is_triggered_forecast,
                checkClaims.is_triggered_forecast
            );
        }
        precipitation_count_hours_triggered = pickIncludingZero(
            item?.precipitation_count_hours_triggered,
            checkClaims.precipitation_count_hours_triggered,
            ...(hasPrecip
                ? [item?.count_hour_triggered, checkClaims.count_hour_triggered]
                : [])
        );
    }

    return {
        company_name: pickFirstDefined(pr.company_name),
        reference_name: pickFirstDefined(ri.reference_name, ai.name, ai.supplier_name),
        policy_code: pickFirstDefined(pi.policy_code),
        reservation_number: pickFirstDefined(ai.reservation_number),
        first_name: pickFirstDefined(ci.first_name),
        last_name: pickFirstDefined(ci.last_name),
        email: pickFirstDefined(ci.email),
        account_holder_name: pickFirstDefined(ci.account_holder_name),
        weather_parameter: formatWeatherParameter(weatherParams),
        trigger_number_hours: pickIncludingZero(
            item?.trigger_number_hours,
            checkClaims.trigger_number_hours,
            sub.trigger_number_hours
        ),
        // Seuil d'intensité par heure
        prcp_trigger_hour_inf: triggerHourInf,
        trigger_low: triggerLow,
        temperature_trigger_hour_inf: pickFirstDefined(
            item?.temperature_trigger_hour_inf,
            checkClaims.temperature_trigger_hour_inf,
            sub.temperature_trigger_hour_inf
        ),
        temperature_trigger_number_hours: pickIncludingZero(
            item?.temperature_trigger_number_hours,
            checkClaims.temperature_trigger_number_hours,
            sub.temperature_trigger_number_hours
        ),
        is_triggered_forecast: pickIncludingZero(
            item?.is_triggered_forecast,
            checkClaims.is_triggered_forecast
        ),
        preferred_language: pickFirstDefined(pi.preferred_language),
        activity_address: pickFirstDefined(
            ai.combined_address,
            buildActivityCombinedAddress(ai)
        ),
        latitude: pickFirstDefined(ai.latitude, fi.latitude),
        longitude: pickFirstDefined(ai.longitude, fi.longitude),
        activity_price: pickFirstDefined(ai.activity_price, ai.trip_price),
        new_trip_price: pickFirstDefined(ai.new_trip_price),
        activity_start_date: pickFirstDefined(ai.activity_start_date),
        activity_end_date: pickFirstDefined(ai.activity_end_date),
        new_trip_start_date: pickFirstDefined(ai.new_trip_start_date),
        new_trip_end_date: pickFirstDefined(ai.new_trip_end_date),
        trip_effective_start_date: pickFirstDefined(ai.trip_effective_start_date),
        trip_effective_end_date: pickFirstDefined(ai.trip_effective_end_date),
        new_trip_effective_start_date: pickFirstDefined(ai.new_trip_effective_start_date),
        new_trip_effective_end_date: pickFirstDefined(ai.new_trip_effective_end_date),
        trip_number_nights: pickIncludingZero(pi.trip_number_nights),
        nb_nights_triggered: pickIncludingZero(pi.nb_nights_triggered),
        new_trip_number_nights: pickIncludingZero(pi.new_trip_number_nights),
        new_nb_nights_triggered: pickIncludingZero(pi.new_nb_nights_triggered),
        trip_modified: pickIncludingZero(ai.trip_modified),
        daily_sum_insured_maintain: pickFirstDefined(pi.daily_sum_insured_maintain),
        daily_sum_insured_cancellation: pickFirstDefined(pi.daily_sum_insured_cancellation),
        total_sum_insured_maintain: pickFirstDefined(pi.total_sum_insured_maintain),
        total_sum_insured_cancellation: pickFirstDefined(pi.total_sum_insured_cancellation),
        weather_data: stringifyJson(fi.weather_data),
        weather_temp: stringifyJson(fi.weather_temp),
        count_hour_triggered: pickIncludingZero(
            item?.count_hour_triggered,
            checkClaims.count_hour_triggered,
            item?.count_hours_triggered,
            checkClaims.count_hours_triggered
        ),
        temperature_is_triggered_forecast,
        temperature_count_hours_triggered,
        precipitation_is_triggered_forecast,
        precipitation_count_hours_triggered,
    };
};

/**
 * @param {object|array} payload — réponse GET api/trips/check-claims
 * @returns {object[]}
 */
export const flattenTripsCheckClaimsForExcel = (payload) => {
    const candidates = [
        payload?.check_claims,
        payload?.data?.check_claims,
        payload?.data,
        payload?.results,
        Array.isArray(payload) ? payload : null,
    ];
    const list = candidates.find((entry) => Array.isArray(entry)) || [];
    return list.map(buildTripsCheckClaimExcelRow);
};
